import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { Building, CircleAlert, Globe, Info, Mail, Plus, Network } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { AuthService } from '@/services/core/auth'
import { PoliticalEntitiesApi } from '@/services/political/politicalEntitiesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { PoliticalEntity } from '../../types/models'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'

const TYPE_STYLES: Record<PoliticalEntity['type'], { bg: string; color: string }> = {
  CONTROLLER: { bg: 'rgba(47,109,244,0.1)', color: '#2f6df4' },
  SPONSOR: { bg: 'rgba(22,163,74,0.1)', color: '#16a34a' },
  PAYER: { bg: 'rgba(234,88,12,0.1)', color: '#ea580c' },
}

function TypeBadge({ type }: { type: PoliticalEntity['type'] }) {
  const { t } = useTranslation()
  const s = TYPE_STYLES[type]
  const labelMap: Record<PoliticalEntity['type'], string> = {
    CONTROLLER: t('political.typeController'),
    SPONSOR: t('political.typeSponsor'),
    PAYER: t('political.typePayer'),
  }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        background: s.bg,
        color: s.color,
      }}
    >
      {labelMap[type]}
    </span>
  )
}

function ParentCell({ entity }: { entity: PoliticalEntity }) {
  if (!entity.parentName) return <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
      <Network className="size-3" style={{ color: 'var(--ad-text-muted)' }} />
      {entity.parentName}
    </span>
  )
}

function ContactCell({ entity }: { entity: PoliticalEntity }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {entity.contactEmail && (
        <a
          href={`mailto:${entity.contactEmail}`}
          style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Mail className="size-3" />
          {entity.contactEmail}
        </a>
      )}
      {entity.siteUrl && (
        <a
          href={entity.siteUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 12,
            color: 'var(--ad-text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Globe className="size-3" />
          {entity.siteUrl.replace(/^https?:\/\//, '')}
        </a>
      )}
      {!entity.contactEmail && !entity.siteUrl && (
        <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
      )}
    </div>
  )
}

function LinkedCell({ entity, allEntities }: { entity: PoliticalEntity; allEntities: PoliticalEntity[] }) {
  const { t } = useTranslation()
  const children = allEntities.filter((e) => e.parentId === entity.id)
  if (children.length === 0) return <span style={{ color: 'var(--ad-text-faint)' }}>—</span>

  const byType: Record<string, number> = {}
  const labelMap: Record<string, string> = {
    CONTROLLER: t('political.typeController'),
    SPONSOR: t('political.typeSponsor'),
    PAYER: t('political.typePayer'),
  }
  for (const c of children) {
    byType[c.type] = (byType[c.type] || 0) + 1
  }

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {Object.entries(byType).map(([type, count]) => {
        const s = TYPE_STYLES[type as PoliticalEntity['type']]
        return (
          <span
            key={type}
            style={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 10px',
              borderRadius: 999,
              letterSpacing: '0.02em',
              background: s.bg,
              color: s.color,
            }}
          >
            {count} {labelMap[type] ?? type}
          </span>
        )
      })}
    </div>
  )
}

export function PoliticalEntitiesPage() {
  const navigate = useNavigate()
  const hasToken = AuthService.isAuthenticated()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [search, setSearch] = useUrlSearch()
  const {
    data: entities = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['politicalEntities', search],
    queryFn: () => PoliticalEntitiesApi.getAll(search),
    enabled: hasToken,
  })

  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.politicalEntities.status', 'ACTIVE')
  const [selection, setSelection] = useState<PoliticalEntity[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const filtered = statusFilter === 'all' ? entities : entities.filter((e) => e.objectState === statusFilter)

  const controllers = filtered.filter((e) => e.type === 'CONTROLLER').length
  const sponsors = filtered.filter((e) => e.type === 'SPONSOR').length
  const payerCount = filtered.filter((e) => e.type === 'PAYER').length

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((e) =>
          PoliticalEntitiesApi.update(e.id, {
            name: e.name,
            type: e.type,
            description: e.description,
            legalEntity: e.legalEntity,
            contactEmail: e.contactEmail,
            address: e.address,
            siteUrl: e.siteUrl,
            vatId: e.vatId,
            ownerTeamId: e.ownerTeamId,
            parentId: e.parentId,
            objectState: state,
          })
        )
      )
      queryClient.invalidateQueries({ queryKey: ['politicalEntities'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'political.doneDetail_activate' : 'political.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const columns: ColumnDef<PoliticalEntity, unknown>[] = [
    selectionColumn<PoliticalEntity>(),
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
      meta: { minWidth: 200 },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => <TypeBadge type={row.original.type} />,
      size: 120,
    },
    {
      accessorKey: 'parentName',
      header: 'Parent',
      cell: ({ row }) => <ParentCell entity={row.original} />,
      enableSorting: false,
      meta: { minWidth: 160 },
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: ({ row }) => <ContactCell entity={row.original} />,
      enableSorting: false,
      meta: { minWidth: 200 },
    },
    {
      id: 'linked',
      header: 'Linked',
      cell: ({ row }) => <LinkedCell entity={row.original} allEntities={entities} />,
      enableSorting: false,
      meta: { minWidth: 140 },
    },
    {
      accessorKey: 'ownerTeamName',
      header: 'Owner Team',
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.ownerTeamName}
        </span>
      ),
    },
    {
      accessorKey: 'networkName',
      header: 'Network',
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.networkName}
        </span>
      ),
    },
    optionalColumn<PoliticalEntity>({
      accessorKey: 'objectState',
      header: 'Status',
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'legalEntity',
      header: 'Legal entity',
      size: 140,
      cell: ({ row }) => <MutedCell value={row.original.legalEntity} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'vatId',
      header: 'VAT ID',
      size: 140,
      cell: ({ row }) => <MutedCell value={row.original.vatId} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'siteUrl',
      header: 'Website',
      meta: { minWidth: 160 },
      cell: ({ row }) => <MutedCell value={row.original.siteUrl} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'description',
      header: 'Description',
      meta: { minWidth: 180 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'address.country',
      header: 'Country',
      size: 120,
      cell: ({ row }) => <MutedCell value={row.original.address?.country} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'address.city',
      header: 'City',
      size: 130,
      cell: ({ row }) => <MutedCell value={row.original.address?.city} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'updateTime',
      header: 'Updated',
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<PoliticalEntity>({
      accessorKey: 'createTime',
      header: 'Created',
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <AppShell crumbs={[{ label: 'Political Entities' }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>Political Entities</h1>
            <p>Organisations registered as political entities in the network.</p>
          </div>
          <div className="ad-page__actions">
            <Button
              onClick={() => navigate(`/political/entities/${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`)}
            >
              <Plus className="size-4" />
              New Entity
            </Button>
          </div>
        </div>

        {error && (
          <div className="auth-form__error" style={{ marginBottom: 16 }}>
            <CircleAlert className="size-4" />
            {(error as Error).message}
          </div>
        )}
        {!hasToken && (
          <div className="auth-form__notice" style={{ marginBottom: 16 }}>
            <Info className="size-4 notice-icon" />
            <span>Log in via the API to view political entities.</span>
          </div>
        )}

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Building} />
                Political Entities
              </>
            }
            count={filtered.length}
            subtitle={
              filtered.length > 0
                ? `${controllers} controller${controllers !== 1 ? 's' : ''} · ${sponsors} sponsor${sponsors !== 1 ? 's' : ''} · ${payerCount} payer${payerCount !== 1 ? 's' : ''}`
                : 'Fetching from network…'
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => { setSearch(v); setSelection([]) }} />
                <StatusFilterButton value={statusFilter} onChange={setStatusFilter} />
              </>
            }
          />

          <CardContent className="p-0">
            <BulkActionBar
              count={selection.length}
              onClear={() => setSelection([])}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
            />
            <DataTable
              data={filtered}
              columns={columns}
              loading={isLoading}
              emptyMessage={
                hasToken
                  ? 'No political entities found in this network.'
                  : 'Sign in via the API to load data.'
              }
              rowHref={(row) => `/political/entities/${row.id}`}
              columnsKey="politicalEntities"
              selection={selection}
              onSelectionChange={setSelection}
            />
          </CardContent>

          {filtered.length > 0 && (
            <CardFooter>
              <span style={{ fontSize: 12, color: 'var(--ad-text-muted)' }}>
                {filtered.length} entit{filtered.length !== 1 ? 'ies' : 'y'}
              </span>
            </CardFooter>
          )}
        </ListCard>
      </div>
    </AppShell>
  )
}
